/**
 * Auth context — the single source of truth for the signed-in user.
 *
 * Loads GET /api/me on mount. A 401 is a normal "not signed in" state, not an
 * error: `user` becomes null and `loading` false.
 *
 * Usage:
 *   const { user, loading, login, logout, refresh } = useAuth();
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError } from '@/lib/api';
import type { User } from '@/lib/types';

export interface AuthContextValue {
  /** The signed-in user, or null when unauthenticated. */
  user: User | null;
  /** True until the initial /api/me check resolves. */
  loading: boolean;
  /** Authenticate; on success updates `user` and returns it. */
  login: (email: string, password: string) => Promise<User>;
  /** End the session and clear `user`. */
  logout: () => Promise<void>;
  /** Re-fetch /api/me (e.g. after changing password). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        // Network / server error — treat as signed-out but don't crash.
        setUser(null);
        if (import.meta.env.DEV) console.error('auth refresh failed', err);
      }
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await refresh();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await api.login(email, password);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
